import { useMutation } from '@apollo/client';
import { Switch } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { gql } from '__generated__/gql';
import classNames from 'classnames';
import { FC, useState } from 'react';

import Badge from 'primitives/Badge';

import {
  CATEGORY_CONFIG,
  CATEGORY_SUBCATEGORIES,
  CHANNELS,
  Channel,
  SUBCATEGORY_LABELS,
} from './notification-category-config';

const UPDATE_CATEGORY_PREFERENCE = gql(`
  mutation AdminUpdateUserNotificationCategoryPreferences(
    $userId: ID!
    $category: Int!
    $subcategory: Int!
    $emailEnabled: Boolean
    $smsEnabled: Boolean
    $pushEnabled: Boolean
    $whatsappEnabled: Boolean
  ) {
    updateUserNotificationCategoryPreferences(
      userId: $userId
      category: $category
      subcategory: $subcategory
      emailEnabled: $emailEnabled
      smsEnabled: $smsEnabled
      pushEnabled: $pushEnabled
      whatsappEnabled: $whatsappEnabled
    ) {
      category
      subcategories {
        id
        category
        subcategory
        emailEnabled
        smsEnabled
        pushEnabled
        whatsappEnabled
      }
    }
  }
`);

const BATCH_UPDATE_CATEGORY_PREFERENCE = gql(`
  mutation AdminBatchUpdateUserNotificationCategoryPreferences(
    $userId: ID!
    $category: Int!
    $emailEnabled: Boolean
    $smsEnabled: Boolean
    $pushEnabled: Boolean
    $whatsappEnabled: Boolean
  ) {
    batchUpdateUserNotificationCategoryPreferences(
      userId: $userId
      category: $category
      emailEnabled: $emailEnabled
      smsEnabled: $smsEnabled
      pushEnabled: $pushEnabled
      whatsappEnabled: $whatsappEnabled
    ) {
      category
      subcategories {
        id
        category
        subcategory
        emailEnabled
        smsEnabled
        pushEnabled
        whatsappEnabled
      }
    }
  }
`);

type SubcategoryPref = {
  id: string;
  category: number;
  subcategory: number;
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  whatsappEnabled: boolean;
};

type CategoryPref = {
  category: number;
  subcategories: SubcategoryPref[];
};

type LocalState = Record<number, Record<number, Record<Channel, boolean>>>;

function buildLocalState(categories: CategoryPref[]): LocalState {
  const state: LocalState = {};
  // Seed defaults for all known categories/subcategories
  for (const [catKey, subs] of Object.entries(CATEGORY_SUBCATEGORIES)) {
    const cat = Number(catKey);
    state[cat] = {};
    for (const sub of subs) {
      state[cat][sub] = {
        emailEnabled: true,
        smsEnabled: false,
        pushEnabled: false,
        whatsappEnabled: false,
      };
    }
  }
  // Override with persisted values
  for (const cat of categories) {
    for (const sub of cat.subcategories) {
      if (!state[cat.category]) state[cat.category] = {};
      state[cat.category][sub.subcategory] = {
        emailEnabled: sub.emailEnabled,
        smsEnabled: sub.smsEnabled,
        pushEnabled: sub.pushEnabled,
        whatsappEnabled: sub.whatsappEnabled,
      };
    }
  }
  return state;
}

function isCategoryChannelOn(state: LocalState, category: number, channel: Channel): boolean {
  const subs = state[category];
  if (!subs) return false;
  return Object.values(subs).some(s => s[channel]);
}

function ToggleSwitch({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Switch
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className={classNames(
        checked ? 'bg-indigo-600' : 'bg-gray-200',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
      )}
    >
      <span
        aria-hidden="true"
        className={classNames(
          checked ? 'translate-x-5' : 'translate-x-0',
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
        )}
      />
    </Switch>
  );
}

const NotificationCategoryMatrix: FC<{
  userId: string;
  categories: CategoryPref[];
}> = ({ userId, categories = [] }) => {
  const [localState, setLocalState] = useState<LocalState>(() => buildLocalState(categories));
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const [updateCategoryPreference] = useMutation(UPDATE_CATEGORY_PREFERENCE);
  const [batchUpdateCategoryPreference] = useMutation(BATCH_UPDATE_CATEGORY_PREFERENCE);

  const toggleExpanded = (category: number) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleSubcategoryToggle = (
    category: number,
    subcategory: number,
    channel: Channel,
    value: boolean
  ) => {
    setLocalState(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subcategory]: {
          ...prev[category][subcategory],
          [channel]: value,
        },
      },
    }));
    updateCategoryPreference({
      variables: { userId, category, subcategory, [channel]: value },
    });
  };

  const handleCategoryToggle = (category: number, channel: Channel, value: boolean) => {
    setLocalState(prev => {
      const updatedSubs: Record<number, Record<Channel, boolean>> = {};
      for (const [subKey, subVal] of Object.entries(prev[category])) {
        updatedSubs[Number(subKey)] = { ...subVal, [channel]: value };
      }
      return { ...prev, [category]: updatedSubs };
    });
    batchUpdateCategoryPreference({
      variables: { userId, category, [channel]: value },
    });
  };

  return (
    <div className="ring-1 ring-gray-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="border-b border-gray-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 w-1/2">
              Category
            </th>
            {CHANNELS.map(({ key, label, beta }) => (
              <th
                key={key}
                className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                <div className="flex flex-col items-center gap-1">
                  {label}
                  {beta && <Badge label="Beta" color="yellow" />}
                </div>
              </th>
            ))}
            <th className="w-8" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {Object.keys(CATEGORY_CONFIG)
            .map(Number)
            .map(category => {
              const config = CATEGORY_CONFIG[category];
              const isExpanded = expandedCategories.has(category);
              const subcategoryIds = CATEGORY_SUBCATEGORIES[category] ?? [];

              return (
                <>
                  <tr key={`cat-${category}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-gray-900">
                        {config?.label ?? `Category ${category}`}
                      </p>
                      <p className="text-sm text-gray-500">{config?.description}</p>
                    </td>
                    {CHANNELS.map(({ key }) => (
                      <td key={key} className="px-4 py-4 text-center">
                        <div className="flex justify-center">
                          <ToggleSwitch
                            checked={isCategoryChannelOn(localState, category, key)}
                            onChange={val => handleCategoryToggle(category, key, val)}
                            disabled={key !== 'emailEnabled'}
                          />
                        </div>
                      </td>
                    ))}
                    <td className="pr-4 py-4 text-center">
                      <button
                        onClick={() => toggleExpanded(category)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? (
                          <ChevronUpIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                  </tr>
                  {isExpanded &&
                    subcategoryIds.map(subcategory => (
                      <tr key={`sub-${subcategory}`} className="bg-gray-50">
                        <td className="pl-10 pr-6 py-3">
                          <p className="text-sm text-gray-700">
                            {SUBCATEGORY_LABELS[subcategory] ?? `Subcategory ${subcategory}`}
                          </p>
                        </td>
                        {CHANNELS.map(({ key }) => (
                          <td key={key} className="px-4 py-3 text-center">
                            <div className="flex justify-center">
                              <ToggleSwitch
                                checked={localState[category]?.[subcategory]?.[key] ?? false}
                                onChange={val =>
                                  handleSubcategoryToggle(category, subcategory, key, val)
                                }
                                disabled={key !== 'emailEnabled'}
                              />
                            </div>
                          </td>
                        ))}
                        <td />
                      </tr>
                    ))}
                </>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};

export default NotificationCategoryMatrix;
